import { DndContext } from '@dnd-kit/core';
import {
  SortableContext,
  verticalListSortingStrategy,
} from '@dnd-kit/sortable';
import { ID } from '@griply/types';
import { sortItems } from '@griply/utils';
import React from 'react';
import { useSortableItemsControls } from 'shared/hooks/use-sortable-items-controls';
import { useSortableSensors } from 'shared/hooks/use-sortable-sensors';
import { verticalAxisModifier } from 'shared/lib/dnd-kit';
import { NewSubTask } from 'shared/types/task';
import { v4 as uuidv4 } from 'uuid';

import { NewSubTaskCard } from './new-sub-task-card';
import { NewTaskSubTask } from './new-task-sub-task';
import * as Styled from './reorderable-sub-task-list.style';

export type NewTaskSubTaskListProps = {
  subTasks: NewSubTask[];
  onChangeSubTasks: (subTasks: NewSubTask[]) => void;
  updateNewSubTask?: (name: string) => void;
};

export const NewTaskSubTaskList: React.FC<NewTaskSubTaskListProps> = ({
  subTasks,
  onChangeSubTasks,
  updateNewSubTask,
}) => {
  const onReorder = (ids: ID[]) =>
    onChangeSubTasks(sortItems(subTasks, ids, 'id'));
  const onCreate = (name: string) =>
    onChangeSubTasks([...subTasks, { id: uuidv4(), name }]);
  const onDeleteSubTask = (id: ID) =>
    onChangeSubTasks(subTasks.filter((task) => task.id !== id));
  const onChangeSubTask = (id: ID, name: string) =>
    onChangeSubTasks(
      subTasks.map((subTask) => ({
        ...subTask,
        name: id === subTask.id ? name : subTask.name,
      })),
    );

  const { items, handleDragStart, handleDragCancel, handleDragEnd } =
    useSortableItemsControls({ items: subTasks, onDragEnd: onReorder });
  const sensors = useSortableSensors();

  return (
    <DndContext
      onDragStart={handleDragStart}
      onDragEnd={handleDragEnd}
      onDragCancel={handleDragCancel}
      sensors={sensors}
      modifiers={verticalAxisModifier}
    >
      <SortableContext
        items={items.map(({ id }) => id)}
        strategy={verticalListSortingStrategy}
      >
        <Styled.List>
          {items.map((task) => (
            <Styled.ListItem key={task.id}>
              <NewTaskSubTask
                id={task.id}
                name={task.name}
                onChange={onChangeSubTask}
                onDelete={onDeleteSubTask}
              />
            </Styled.ListItem>
          ))}
          <Styled.ListItem>
            <NewSubTaskCard onSubmit={onCreate} onChange={updateNewSubTask} />
          </Styled.ListItem>
        </Styled.List>
      </SortableContext>
    </DndContext>
  );
};
